import React from 'react';
import { CornerDownRightIcon, RadioIcon, NetworkIcon, Loader2Icon } from 'lucide-react';
import { getCapacityLabel, getCapacityTier } from '../utils/linkCapacity';
import { getAlproLabel, isMetro } from '../utils/alpro';
import { countLeaves, countUpstream, maxDepth, type UpstreamNode } from '../utils/upstream';
interface UpstreamTreeProps {
  rootSiteId: string;
  nodes: UpstreamNode[];
  loading: boolean;
  truncated: boolean;
  onTraceToMetro?: (siteId: string) => void;
  onOpenSite?: (siteId: string) => void;
}
export function UpstreamTree({
  rootSiteId,
  nodes,
  loading,
  truncated,
  onTraceToMetro,
  onOpenSite
}: UpstreamTreeProps) {
  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-950/50 p-3 rounded-lg" data-id="element-571" data-mp="3a7ipmwo98bla">
        <Loader2Icon size={13} className="animate-spin text-cyan-400" data-id="element-572" data-mp="1auodokhbxzlk" />
        Menelusuri rantai radio sampai ujung…
      </div>;
  }
  if (!nodes.length) {
    return <div className="text-slate-500 text-xs bg-slate-950/50 p-3 rounded-lg" data-id="element-573" data-mp="un2vd256hdkh">
        Tidak ada NE lain yang mengarah ke site ini.
      </div>;
  }
  return <div className="bg-slate-950/50 p-3 rounded-lg" data-id="element-574" data-mp="pv2xdf8o4uqj">
      <div className="flex flex-wrap gap-2 mb-3" data-id="element-575" data-mp="lu9amp6yplz8">
        <Stat label="Total site hulu" value={countUpstream(nodes)} data-id="element-576" data-mp="lv0y1cqeodef" />
        <Stat label="Ujung radio" value={countLeaves(nodes)} accent data-id="element-577" data-mp="gwr22zwux8rr" />
        <Stat label="Kedalaman" value={`${maxDepth(nodes)} level`} data-id="element-578" data-mp="1dmgqvoik2wd3" />
      </div>

      <div className="text-[10px] font-mono text-slate-500 mb-2 truncate" data-id="element-579" data-mp="19kdcos4i5do4">
        Muara: {rootSiteId}
      </div>

      <ul className="space-y-1.5" data-id="element-580" data-mp="14l0itnf7r6c1">
        {nodes.map(node => <TreeRow key={`${node.siteId}-${node.depth}`} node={node} onTraceToMetro={onTraceToMetro} onOpenSite={onOpenSite} data-id="element-581" data-mp="14nd3i8ccvuss" />)}
      </ul>

      {truncated && <p className="text-[10px] text-amber-300/80 mt-3" data-id="element-582" data-mp="3kcodfc6r8mro">
          Rantai sangat besar — sebagian cabang terjauh tidak ditampilkan.
        </p>}
    </div>;
}
function TreeRow({
  node,
  onTraceToMetro,
  onOpenSite
}: {
  node: UpstreamNode;
  onTraceToMetro?: (siteId: string) => void;
  onOpenSite?: (siteId: string) => void;
}) {
  const tier = getCapacityTier(node.link);
  const metro = isMetro(node.link.alpro_type);
  const isLeaf = node.children.length === 0;
  return <li data-id="element-583" data-mp="3332k9sy9cyxj">
      <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 transition-colors" style={{
      marginLeft: `${(node.depth - 1) * 14}px`
    }} data-id="element-584" data-mp="333c7j8rx4rzj">
        <CornerDownRightIcon size={12} className="text-slate-600 flex-shrink-0" data-id="element-585" data-mp="2u374fatrcfr6" />

        {metro ? <NetworkIcon size={12} className="text-cyan-400 flex-shrink-0" data-id="element-586" data-mp="2y4941srl6tto" /> : <RadioIcon size={12} className="text-orange-400 flex-shrink-0" data-id="element-587" data-mp="2k4q8s0jvjcys" />}

        <div className="min-w-0 flex-1" data-id="element-588" data-mp="2p29mmzcntnq7">
          <div className="text-xs font-mono font-semibold text-white truncate" data-id="element-589" data-mp="2p5e0kofxfxu2">
            {node.siteId}
          </div>
          <div className="text-[10px] text-slate-500 truncate" data-id="element-590" data-mp="20iocal51ecc3">
            → {node.link.site_id_fe} · {getAlproLabel(node.link.alpro_type)}
          </div>
        </div>

        <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{
        color: tier.color,
        background: `${tier.color}1f`,
        border: `1px solid ${tier.color}55`
      }} data-id="element-591" data-mp="2fdm85nze9jxx">
          {getCapacityLabel(node.link)}
        </span>

        {isLeaf && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 flex-shrink-0" data-id="element-592" data-mp="2y6gb1jjqtlls">
            UJUNG
          </span>}

        <div className="flex gap-1 flex-shrink-0" data-id="element-593" data-mp="376d6c40n1ipt">
          <button onClick={() => onTraceToMetro?.(node.siteId)} title="Tampilkan jalur ke Metro" className="px-1.5 py-1 rounded-md bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 text-[9px] font-semibold hover:bg-cyan-500/20 transition-colors" data-id="element-594" data-mp="3774rsm0iu02d">
            Jalur
          </button>
          <button onClick={() => onOpenSite?.(node.siteId)} title="Buka detail site ini" className="px-1.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-300 text-[9px] font-semibold hover:bg-slate-700 transition-colors" data-id="element-595" data-mp="1a8ny83nhnbez">
            Detail
          </button>
        </div>
      </div>

      {node.children.length > 0 && <ul className="space-y-1.5 mt-1.5" data-id="element-596" data-mp="mko6b0efxeyt">
          {node.children.map(child => <TreeRow key={`${child.siteId}-${child.depth}`} node={child} onTraceToMetro={onTraceToMetro} onOpenSite={onOpenSite} data-id="element-597" data-mp="rk0rzhtksfzt" />)}
        </ul>}
    </li>;
}
function Stat({
  label,
  value,
  accent
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return <div className="px-2.5 py-1.5 rounded-lg bg-slate-900/80 border border-slate-800" data-id="element-598" data-mp="3rg7hl4e6akh7">
      <div className="text-[9px] text-slate-500 uppercase tracking-[0.12em] font-semibold" data-id="element-599" data-mp="3ng6vcxku14zl">
        {label}
      </div>
      <div className={`text-xs font-mono font-bold ${accent ? 'text-cyan-300' : 'text-slate-200'}`} data-id="element-600" data-mp="3imkoxn59hxc8">
        {value}
      </div>
    </div>;
}